import { useEffect, useMemo, useState } from "react";
import { TranslationService } from "../../../../services/TranslationService";
import { EditProps } from "../EditAction";
import { Field } from "./FieldHelpers";
import Dropdown from "../../../shared/components/Dropdown";
import WhatsappService, {
  WhatsappTemplateListType,
  WhatsappTemplateItemType
} from "../../../../services/WhatsappService";
import { ToastService } from "../../../shared/bootstrap/Toast";
import CompanyService, { Entities } from "../../../../services/CompanyService";
import {
  EntityField,
  sortEntityFields
} from "../../../shared/entities/EntityField";
import {
  ClientFields,
  getClientFieldTitle
} from "../../../../services/ClientService";
import { ValidationMessage } from "../../../shared/RequieredManager";

export const SelectWhatsappTemplate = (props: EditProps) => {
  const [data, setData] = useState<WhatsappTemplateListType>();
  const hasDefaultValue =
    (props.data as Object).hasOwnProperty("WATemplateID") &&
    (props.data as Object).hasOwnProperty("Content");
  const selectOptionTranslate =
    TranslationService.translate.Select +
    " " +
    TranslationService.translate.WhatsAppTemplate;

  const fixedFieldsCustomer = useMemo(() => {
    const clientFieldOptions = [
      ...CompanyService.getAdditionalDefinitionsFiltered(Entities.Client).map(
        (x) => new EntityField(x)
      )
    ]
      .sort(sortEntityFields(CompanyService.getPersonSortedFields().split(",")))
      .map((x) => ({ value: x.id.toString(), text: x?.title ?? "" }));

    const insertFieldOptions = [
      { text: TranslationService.translate.InsertField, value: "" },
      ...WhatsappService.getConfigItemsByWhatsApp().map((cl) => ({
        value: cl.toString(),
        text: getClientFieldTitle(cl as ClientFields)
      })),
      ...clientFieldOptions
    ];
    return insertFieldOptions;
  }, []);

  // Función auxiliar para procesar el template y reemplazar campos
  const processTemplate = (item: WhatsappTemplateItemType) => {
    const dataParameters = item.Parameters;
    const elements = Array.from(
      { length: dataParameters || 0 },
      (_, index) => index + 1
    );
    let texto = item.Structure || "";

    if (item.FieldList) {
      const fieldListArray = item.FieldList.split(",");
      elements.forEach((elIndex) => {
        const param = elIndex;
        const fieldValue = fieldListArray[param - 1];
        const fieldText = fixedFieldsCustomer.find(
          (f) => f.value === fieldValue?.trim()
        )?.text;
        if (fieldText) {
          texto = texto.replace(`{{${param}}}`, `{{${fieldText}}}`);
        }
      });
    }

    return texto;
  };

  const onChange = (value: string) => {
    if (value === selectOptionTranslate) {
      props.setData((old: any) => {
        delete old.Content;
        delete old.WATemplateID;
        return { ...old, Type: 7 };
      });
    } else if (value?.length > 0) {
      const valueSelected = value.split("---");
      const item =
        data?.list &&
        data.list.find((el) => el.WATemplateID.toString() === valueSelected[0]);

      if (item) {
        const texto = processTemplate(item);
        props.setData((old: any) => ({
          ...old,
          Content: texto,
          WATemplateID: parseInt(valueSelected[0]),
          Type: 7
        }));
      }
    }
  };

  const templateValidation = props.requiredManager.makeRequiredWithId(
    (value: string) => {
      onChange(value);
    },
    "WATemplateID"
  );

  const handleSelectedChange = (value: any) => {
    const valueSelected = value.split("---");
    const item =
      data?.list &&
      data.list.find((el) => el.WATemplateID.toString() === valueSelected[0]);

    if (item) {
      const texto = processTemplate(item);
      props.setData((old: any) => ({
        ...old,
        Content: texto,
        WATemplateID: parseInt(valueSelected[0]),
        Type: 7
      }));
    }

    templateValidation(value);
  };

  useEffect(() => {
    if (hasDefaultValue && data?.list && props.data.WATemplateID) {
      const template = data.list.find(
        (t) => t.WATemplateID === Number(props.data.WATemplateID)
      );
      if (template) {
        const texto = processTemplate(template);
        props.setData((old: any) => ({ ...old, Content: texto }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.list, hasDefaultValue]);

  useEffect(() => {
    const getWhatsappTemplateList = async () => {
      const result = await WhatsappService.getList();
      if (result instanceof Error) {
        ToastService.showToast(
          "Error al obtener templates",
          undefined,
          "danger"
        );
        return;
      } else {
        setData(result);
      }
    };
    getWhatsappTemplateList();
  }, []);

  const fixedFields = useMemo(() => {
    const templateFieldOptions = data?.list
      .filter((f) => f.Parameters === 0 || f.FieldList)
      .map((x) => ({
        value: `${x.WATemplateID}---${x.Structure}`,
        text: x.Name ?? ""
      }));
    templateFieldOptions?.unshift({
      text:
        TranslationService.translate.Select +
        " " +
        TranslationService.translate.WhatsAppTemplate,
      value:
        TranslationService.translate.Select +
        " " +
        TranslationService.translate.WhatsAppTemplate
    });
    return templateFieldOptions || [];
  }, [data?.list]);

  const insertFieldOptions = useMemo(() => {
    const itemsFields = fixedFields;
    return itemsFields;
  }, [fixedFields]);

  // Calcular el defaultValue correcto usando Structure en lugar de Content
  const defaultValue = useMemo(() => {
    if (!hasDefaultValue || !props.data.WATemplateID || !data?.list) {
      return selectOptionTranslate;
    }
    const template = data.list.find(
      (t) => t.WATemplateID === Number(props.data.WATemplateID)
    );
    if (template && template.Structure) {
      return `${template.WATemplateID}---${template.Structure}`;
    }
    return selectOptionTranslate;
  }, [
    hasDefaultValue,
    props.data.WATemplateID,
    data?.list,
    selectOptionTranslate
  ]);

  return (
    <div className="row">
      <div className={"col-6"}>
        <Field
          colClass={"col"}
          title={TranslationService.translate.WhatsAppTemplate}
        >
          {data && (
            <Dropdown
              defaultValue={defaultValue}
              onChange={handleSelectedChange}
              placeholder={selectOptionTranslate}
              items={insertFieldOptions}
            />
          )}
          <ValidationMessage
            onChange={templateValidation}
            message={TranslationService.translate.SelectWhatsAppTemplate}
            validationMethod={(value: string | undefined) => {
              // Validar que se haya seleccionado un template válido (no la opción de "Seleccionar")
              return (
                value !== undefined &&
                value !== selectOptionTranslate &&
                value.length > 0 &&
                props.data.WATemplateID !== undefined
              );
            }}
            defaultValue={defaultValue}
          />
        </Field>
      </div>
    </div>
  );
};
